import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { Copy, ExternalLink } from 'lucide-react';

import EnvironmentEcho from '../EnvironmentEcho';
import {
  TenantAppAction,
  TenantAppPhase,
  useTenantAppSignIn,
} from '../../hooks/useTenantAppSignIn';
import type { TenantAppSession } from '../../api/tenantRegistry';
import { beginTenantAppRedirect, tenantAppErrorCode } from '../../api/tenantRegistry';
import { useOptionalEnvironment } from '../../context/EnvironmentContext';
import { callbackRedirectUri, purposeForAction, stashSignIn } from '../../lib/tenantConnectRedirect';
import { errorText } from '../../lib/errorText';
import { IDENTIFIER, STROKE, SURFACE, TEXT } from '../../theme/tokens';

// FD-530 / FD-531 — the brokered sign-in for creating and rotating a tenant's
// FD-ADR-020 application (`FD-ADR-046` §3, §6).
//
// One dialog for all three actions, because all three are the same interaction:
// a customer's Application Administrator enters a code, and AirBridge does the
// work server-side the moment they finish. Only the copy differs, and the copy
// is where the care goes — each of these acts in a directory AirBridge does not
// own and, under `FD-ADR-046` D1, has no standing authority to undo.

const COPY: Record<
  TenantAppAction,
  { title: string; lede: string; cta: string; done: string }
> = {
  create: {
    title: 'Create the tenant application',
    lede:
      'Creates the application AirBridge authenticates as in this tenant, and generates its certificate in this deployment’s Key Vault. It is created in the CUSTOMER’S directory and owned by them — they can revoke it at any time without involving Rubix.',
    cta: 'Start sign-in',
    done: 'Application created',
  },
  rotate: {
    title: 'Rotate the certificate',
    lede:
      'Generates a new certificate and attaches it BESIDE the existing one. The old key keeps working — a token minted seconds ago is still being presented by work in flight, so nothing is revoked here.',
    cta: 'Start sign-in',
    done: 'Certificate rotated',
  },
  'remove-previous-key': {
    title: 'Remove the superseded key',
    lede:
      'Detaches the old key credential now that tokens signed by it have aged out. Optional — left alone it simply expires. Removing it early is what matters when the rotation followed a suspected compromise.',
    cta: 'Start sign-in',
    done: 'Superseded key removed',
  },
};

export interface TenantApplicationDialogProps {
  open: boolean;
  action: TenantAppAction;
  tenantId: string;
  /** Domain or display name, for the "which directory is this" line. */
  tenantLabel: string;
  onClose: () => void;
}

export default function TenantApplicationDialog({
  open,
  action,
  tenantId,
  tenantLabel,
  onClose,
}: TenantApplicationDialogProps) {
  const { phase, start, submitToken, cancel, guidedDisabled } = useTenantAppSignIn(tenantId);
  // Optional: an environment-less render (a unit test) still works; the stash then
  // records '' and the return keeps whatever environment is active.
  const environmentId = useOptionalEnvironment()?.environmentId ?? '';
  const [announcement, setAnnouncement] = useState('');
  // R-FD-055: the redirect sign-in is the default. Device code and paste stay, as the
  // fallbacks for a tenant that blocks one path or the other.
  const [method, setMethod] = useState<'redirect' | 'guided' | 'paste'>('redirect');
  const [redirecting, setRedirecting] = useState(false);
  const [redirectError, setRedirectError] = useState<unknown>(null);
  const [signInUnavailable, setSignInUnavailable] = useState(false);
  const [token, setToken] = useState('');
  const copy = COPY[action];

  // FD-551. The server has accepted `graphAccessToken` on all three of these endpoints
  // since FD-527 and only the console lacked an arm — so it offered one of the two
  // paths the API supports, and it was THE BLOCKABLE ONE. Microsoft recommends blocking
  // the device-code flow, and a Conditional Access authentication-flows policy will; a
  // customer whose security team has done that could create a tenant application
  // through the API and not through this dialog, and would find out after typing a code.
  //
  // `FD-ADR-032` §6 settled this for the wave dialog and the argument transfers
  // unchanged: keep the paste path FIRST-CLASS in the same dialog, and where the
  // deployment reports the guided path off, HIDE it rather than offer a route that
  // fails late.
  //
  // R-FD-055: GRAPH_DEVICE_CODE_ENABLED=false now hides only the DEVICE-CODE option.
  // It exists because a tenant can block that flow; it never governed the redirect.
  const effectiveMethod = signInUnavailable ? 'paste' : guidedDisabled && method === 'guided' ? 'redirect' : method;

  // The redirect arm: ask the Manager for an authorize URL + verifier, keep the
  // verifier for this one round trip, and leave the page. Microsoft brings the
  // operator back to /tenants/connect/callback, where the Manager redeems. If the
  // stash cannot be written the tab does NOT navigate — the return could never be
  // redeemed, and an operator would sign in for nothing.
  async function startRedirect() {
    setRedirectError(null);
    setRedirecting(true);
    try {
      const session = await beginTenantAppRedirect(tenantId, purposeForAction(action), callbackRedirectUri());
      stashSignIn(session.state, {
        tenantId,
        action,
        codeVerifier: session.codeVerifier,
        redirectUri: session.redirectUri,
        environmentId,
        createdAt: Date.now(),
      });
      window.location.assign(session.authorizeUrl);
    } catch (err) {
      // No provisioning client on this deployment: neither Microsoft sign-in can
      // work here, and retrying will not change that. Say so and move the operator
      // to the arm that needs no provisioning client (a pasted token).
      if (tenantAppErrorCode(err) === 'GRAPH_DEVICE_CODE_NOT_CONFIGURED') {
        setSignInUnavailable(true);
        setMethod('paste');
      } else {
        setRedirectError(err);
      }
      setRedirecting(false);
    }
  }

  // Deliberately NOT cleared on every phase change — only when the dialog closes.
  // A failed submit must leave the token in the box, or the operator re-fetches a
  // token to retry something the server may simply have been unable to reach.
  function close() {
    setAnnouncement('');
    setToken('');
    setMethod('redirect');
    setRedirectError(null);
    setSignInUnavailable(false);
    cancel();
    onClose();
  }

  // Announce phase changes once each, for a screen reader watching a dialog whose
  // visible content changes without any interaction of theirs.
  useEffect(() => {
    if (phase.kind === 'awaiting-signin') setAnnouncement('Sign-in code ready.');
    else if (phase.kind === 'working') setAnnouncement('Signing in completed. Working…');
    else if (phase.kind === 'done') setAnnouncement(copy.done);
    else if (phase.kind === 'expired') setAnnouncement('The sign-in window closed.');
    else if (phase.kind === 'failed') setAnnouncement('The request failed.');
  }, [phase.kind, copy.done]);

  const busy =
    phase.kind === 'starting' || phase.kind === 'awaiting-signin' || phase.kind === 'working';

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4">
              {/* Which DEPLOYMENT is about to act. First, because it decides
                  whether the rest of the dialog should be read at all. */}
              <EnvironmentEcho />

              <div aria-live="polite" className="sr-only">
                {announcement}
              </div>

              <p className="font-display text-[13px] leading-relaxed" style={{ color: TEXT.secondary }}>
                {copy.lede}
              </p>

              {/* Which DIRECTORY. The echo above names the deployment; this names
                  the tenant the application lands in, which is the other half of
                  "am I about to do this in the right place" and is not derivable
                  from the first. */}
              <dl className="flex flex-col gap-1 font-display text-[13px]">
                <div className="flex flex-wrap gap-2">
                  <dt style={{ color: TEXT.secondary }}>Tenant</dt>
                  <dd className="m-0" style={{ color: TEXT.primary }}>
                    {tenantLabel}
                  </dd>
                </div>
                <div className="flex flex-wrap gap-2">
                  <dt style={{ color: TEXT.secondary }}>Tenant ID</dt>
                  <dd className="m-0" style={IDENTIFIER}>
                    {tenantId}
                  </dd>
                </div>
              </dl>

              {phase.kind === 'idle' || phase.kind === 'failed' || phase.kind === 'expired' ? (
                <div className="flex flex-col gap-3">
                  {guidedDisabled && !signInUnavailable ? (
                    <MessageBar intent="info">
                      <MessageBarBody>
                        <MessageBarTitle>The guided sign-in is off for this deployment</MessageBarTitle>
                        Sign in with Microsoft instead, or paste a Graph token. The code-entry flow is
                        blockable per tenant, which is why the other paths exist.
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}

                  <RadioGroup
                    value={effectiveMethod}
                    onChange={(_, d) => setMethod(d.value as 'redirect' | 'guided' | 'paste')}
                    aria-label="How to sign in"
                  >
                    {signInUnavailable ? null : (
                      <Radio
                        value="redirect"
                        label="Sign in with Microsoft — you come straight back here (recommended)"
                      />
                    )}
                    {guidedDisabled || signInUnavailable ? null : (
                      <Radio value="guided" label="Guided sign-in — a code the tenant's admin enters on another device" />
                    )}
                    <Radio value="paste" label="Paste a Graph access token I obtained myself" />
                  </RadioGroup>

                  {effectiveMethod === 'redirect' ? (
                    <p className="font-display text-xs m-0" style={{ color: TEXT.secondary }}>
                      Sign in as an <strong>Application Administrator</strong> (or Global
                      Administrator) of this tenant. The first time, a Global Administrator also
                      approves <em>AirBridge Tenant Provisioning</em> for the organisation.
                      AirBridge receives a one-time code and finishes server-side; the sign-in
                      token never reaches this browser.
                    </p>
                  ) : null}

                  {signInUnavailable ? (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        <MessageBarTitle>Microsoft sign-in is not set up on this deployment</MessageBarTitle>
                        This AirBridge deployment has no provisioning app configured, so it cannot sign
                        you in to create an application. Paste a Graph access token below, or close this
                        and use <strong>Record an existing application</strong>.
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}

                  {redirectError ? (
                    <MessageBar intent="error">
                      <MessageBarBody>
                        <MessageBarTitle>Could not start the sign-in</MessageBarTitle>
                        {errorText(redirectError)}
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}

                  {effectiveMethod === 'paste' ? (
                    <div className="flex flex-col gap-2">
                      <Textarea
                        value={token}
                        onChange={(_, d) => setToken(d.value)}
                        placeholder="eyJ0eXAiOi…"
                        resize="vertical"
                        aria-label="Microsoft Graph access token"
                        textarea={{ rows: 3, spellCheck: false, autoComplete: 'off' }}
                      />
                      {/* Where it comes from, stated rather than assumed — the same
                          obligation ActivateWaveDialog carries for its pasted token.
                          This is the acquisition the rehearsal itself used. */}
                      <p className="font-display text-xs m-0" style={{ color: TEXT.secondary }}>
                        A <strong>delegated</strong> token for this tenant carrying{' '}
                        <code style={IDENTIFIER}>Application.ReadWrite.All</code> — e.g.{' '}
                        <code style={IDENTIFIER}>az login --tenant {tenantId}</code> then{' '}
                        <code style={IDENTIFIER}>
                          az account get-access-token --resource https://graph.microsoft.com
                        </code>
                        . It is sent once and consumed server-side; AirBridge stores nothing.
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <PhaseBody
                phase={phase}
                action={action}
                onCopy={(code) => {
                  void navigator.clipboard?.writeText(code);
                  setAnnouncement('Code copied.');
                }}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>
              {phase.kind === 'done' || phase.kind === 'already-resolved' ? 'Close' : 'Cancel'}
            </Button>
            {phase.kind === 'idle' || phase.kind === 'failed' || phase.kind === 'expired' ? (
              <Button
                appearance="primary"
                onClick={() =>
                  effectiveMethod === 'redirect'
                    ? void startRedirect()
                    : effectiveMethod === 'paste'
                      ? submitToken(action, token)
                      : start(action)
                }
                // The paste arm needs a token before it can do anything. Disabling is
                // right here and would be wrong on the guided arm, which has nothing
                // for the operator to supply.
                disabled={busy || redirecting || (effectiveMethod === 'paste' && token.trim().length === 0)}
              >
                {phase.kind === 'idle'
                  ? effectiveMethod === 'paste'
                    ? 'Submit token'
                    : effectiveMethod === 'redirect'
                      ? redirecting
                        ? 'Opening Microsoft sign-in…'
                        : 'Sign in with Microsoft'
                      : copy.cta
                  : 'Try again'}
              </Button>
            ) : null}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function PhaseBody({
  phase,
  action,
  onCopy,
}: {
  phase: TenantAppPhase;
  action: TenantAppAction;
  onCopy: (code: string) => void;
}) {
  if (phase.kind === 'starting') {
    return (
      <div className="flex items-center gap-3 py-2">
        <Spinner size="tiny" />
        <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Starting the sign-in…
        </span>
      </div>
    );
  }

  // The pasted arm: working, with no session. There is no code to show, no page to
  // open and no countdown — the operator signed in wherever they got the token.
  if (phase.kind === 'working' && !phase.session) {
    return (
      <div className="flex items-center gap-3 py-2">
        <Spinner size="tiny" />
        <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Working in the tenant now. This takes a few seconds.
        </span>
      </div>
    );
  }

  if (phase.kind === 'awaiting-signin' || phase.kind === 'working') {
    // Narrowed by the branch above: a `working` phase reaching here has a session.
    const session = phase.session as TenantAppSession;
    const working = phase.kind === 'working';
    return (
      <div
        className="flex flex-col gap-3 p-4"
        style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.rowFill }}
      >
        <p className="font-display text-[13px] m-0" style={{ color: TEXT.secondary }}>
          An <strong>Application Administrator of this tenant</strong> opens the sign-in page and
          enters this code. Keep the dialog open — AirBridge finishes as soon as they do.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span
            className="font-mono text-2xl font-semibold"
            style={{ color: TEXT.primary, letterSpacing: '0.18em' }}
            // A screen-reader user has to TYPE this. Read as a word it is useless;
            // spaced characters are dictation somebody can follow.
            aria-label={`Code ${session.userCode.split('').join(' ')}`}
          >
            {session.userCode}
          </span>
          <div className="flex items-center gap-2">
            <Button
              appearance="secondary"
              icon={<Copy size={16} strokeWidth={1.5} />}
              onClick={() => onCopy(session.userCode)}
            >
              Copy code
            </Button>
            {/* A real anchor, not window.open — middle-click, copy-link and popup
                blockers all behave, and it needs no JS. */}
            <Button
              as="a"
              appearance="primary"
              href={session.verificationUrl}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ExternalLink size={16} strokeWidth={1.5} />}
            >
              Open sign-in page
            </Button>
          </div>
        </div>

        {working ? (
          <div className="flex items-center gap-2">
            <Spinner size="tiny" />
            <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
              Signed in — working in the tenant now. This takes a few seconds.
            </span>
          </div>
        ) : (
          <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
            Code expires in {formatRemaining(phase.secondsRemaining)}.
          </span>
        )}
      </div>
    );
  }

  if (phase.kind === 'done') {
    const { result } = phase;
    return (
      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>{COPY[action].done}</MessageBarTitle>
          {result.action === 'create' ? (
            <>
              Application <span style={IDENTIFIER}>{result.clientId}</span> now exists in this
              tenant, with its certificate in the deployment&rsquo;s Key Vault.{' '}
              <strong>It cannot do anything yet.</strong> A Privileged Role Administrator or Global
              Administrator must grant consent — AirBridge deliberately holds no token that can
              grant a Graph app role. Use <strong>Trigger admin consent</strong> on the Overview
              tab.
            </>
          ) : result.action === 'rotate' ? (
            <>
              A new certificate is attached and in use. The previous key is still attached and
              still valid; retire it once work signed with it has finished.
            </>
          ) : (
            <>The superseded key is detached. Only the current certificate remains.</>
          )}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (phase.kind === 'already-resolved') {
    return (
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>That key was already gone</MessageBarTitle>
          Nothing needed removing — the application carries no superseded key. The record has been
          corrected, so this tenant no longer reports a pending removal.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (phase.kind === 'expired') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>The sign-in window closed</MessageBarTitle>
          Nothing was written and there is nothing to clean up — the code simply expired. Start
          over for a new one.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (phase.kind === 'failed') {
    return <FailureBody error={phase.error} action={action} />;
  }

  return null;
}

/**
 * Branches only on refusals whose REMEDY differs, and renders the server's own
 * message for everything else.
 *
 * A friendly sentence invented per code is worse than the server's for any code
 * the console does not actually understand — the server's at least came from the
 * code that refused.
 */
function FailureBody({ error, action }: { error: unknown; action: TenantAppAction }) {
  const code = tenantAppErrorCode(error);

  if (code === 'GRAPH_DEVICE_CODE_DISABLED' || code === 'GRAPH_DEVICE_CODE_NOT_CONFIGURED') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>This deployment cannot run a guided sign-in</MessageBarTitle>
          The guided path is unavailable here. Create or rotate the application out of band and
          record it with <strong>Record an existing application</strong>.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'TENANT_APP_CREATE_UNSUPPORTED') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Refused before anything was written</MessageBarTitle>
          This build cannot complete the certificate half, so it declined rather than leave an
          application in the tenant that nothing can authenticate as. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'TENANT_APP_OBJECT_ID_UNKNOWN') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>The application&rsquo;s object ID is not recorded</MessageBarTitle>
          Rotation addresses Graph by OBJECT id, which is a different identifier from the client
          id and not interchangeable with it. Record it with{' '}
          <strong>Record an existing application</strong>, then rotate.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'TENANT_APP_LAST_KEY_REFUSED') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Refused — that is the only credential left</MessageBarTitle>
          Removing it would leave an application in a customer&rsquo;s tenant that nobody can
          authenticate as, with no way back that does not involve another sign-in. Rotate first,
          then remove the superseded key.
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>
          {action === 'create' ? 'Could not create the application' : 'Could not complete the rotation'}
        </MessageBarTitle>
        {errorText(error)}
      </MessageBarBody>
    </MessageBar>
  );
}

function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}
